import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Plus, RefreshCw, Trash2, TrendingUp, TrendingDown, Minus, Loader2, Wand2, ExternalLink, MapPin, Sparkles, Eye, Phone as PhoneIcon, Mail as MailIcon, Zap } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip as RTooltip, PieChart, Pie, Cell } from "recharts";
import { toast } from "sonner";
import { useSettings } from "../../lib/useSettings";

export function RankTrackerAdmin() {
  const { settings: site } = useSettings();
  const [items, setItems] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const load = async () => setItems((await api.get("/keywords")).data);
  useEffect(() => { load(); }, []);

  // Default location from settings once loaded
  useEffect(() => {
    if (site && !location) {
      const city = site.main_city && site.main_city !== "[Votre ville]" ? site.main_city : "";
      setLocation(city ? `${city}, France` : "");
    }
  }, [site]); // eslint-disable-line

  const currentDomain = typeof window !== "undefined" ? window.location.hostname : "";

  const add = async () => {
    if (!keyword) return;
    await api.post("/keywords", { keyword, location, target_domain: currentDomain });
    setKeyword(""); toast.success("Mot-clé ajouté"); load();
  };
  const check = async (id) => {
    setLoading(id);
    try {
      await api.post(`/keywords/${id}/check`);
      toast.success("Position mise à jour");
      load();
    } catch { toast.error("Erreur SerpAPI"); }
    finally { setLoading(null); }
  };
  const checkAll = async () => {
    if (!items.length) return;
    setBatchLoading(true);
    try {
      const { data } = await api.post("/keywords/check-all");
      toast.success(`${data.checked} vérifiés${data.errors ? ` · ${data.errors} erreurs` : ""}`);
      load();
    } catch { toast.error("Échec batch SerpAPI"); }
    finally { setBatchLoading(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce mot-clé ?")) return;
    await api.delete(`/keywords/${id}`); load();
  };

  // Aggregate KPIs
  const withPos = items.filter(k => k.current_position);
  const avgPos = withPos.length ? (withPos.reduce((a, k) => a + k.current_position, 0) / withPos.length).toFixed(1) : "—";
  const inTop3 = items.filter(k => k.current_position && k.current_position <= 3).length;
  const inTop10 = items.filter(k => k.current_position && k.current_position <= 10).length;
  const improved = items.filter(k => {
    const p = k.positions?.[k.positions.length - 2]?.position;
    return p && k.current_position && p - k.current_position > 0;
  }).length;
  const dropped = items.filter(k => {
    const p = k.positions?.[k.positions.length - 2]?.position;
    return p && k.current_position && p - k.current_position < 0;
  }).length;

  // Alerts: position dropped by >3 OR left top 10
  const alerts = items.flatMap(k => {
    const prev = k.positions?.[k.positions.length - 2]?.position;
    const curr = k.current_position;
    if (!prev || !curr) return [];
    const diff = prev - curr;
    const out = [];
    if (prev <= 10 && curr > 10) out.push({ kw: k.keyword, msg: `Sorti du top 10 (#${prev} → #${curr})`, severity: "red" });
    else if (diff < -3) out.push({ kw: k.keyword, msg: `Chute de ${Math.abs(diff)} positions (#${prev} → #${curr})`, severity: "amber" });
    else if (prev > 3 && curr <= 3) out.push({ kw: k.keyword, msg: `Entré dans le TOP 3 (#${prev} → #${curr}) 🎉`, severity: "emerald" });
    return out;
  });

  return (
    <div className="p-6 md:p-10" data-testid="admin-rank-page">
      <header className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">SEO · SerpAPI</div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Rank Tracker</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Suivi des positions Google locales en temps réel.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={checkAll}
            disabled={batchLoading || !items.length}
            className="bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy)]/90 font-bold"
            data-testid="check-all-keywords"
          >
            {batchLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/> Vérification...</> : <><RefreshCw className="w-4 h-4 mr-2"/> Tout vérifier</>}
          </Button>
        </div>
      </header>

      {/* KPI row */}
      <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {[
          { label: "Mots-clés suivis", value: items.length, color: "text-[color:var(--brand-navy)]" },
          { label: "Position moyenne", value: avgPos, color: "text-[color:var(--brand-sky)]" },
          { label: "Top 3", value: inTop3, color: "text-emerald-500" },
          { label: "Top 10", value: inTop10, color: "text-blue-500" },
          { label: "En progression", value: improved, color: "text-emerald-500", Icon: TrendingUp },
          { label: "En régression", value: dropped, color: "text-red-500", Icon: TrendingDown },
        ].map((k, i) => (
          <Card key={i} className="p-4 border">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-semibold">
              {k.Icon && <k.Icon className={`w-3 h-3 ${k.color}`}/>}
              {k.label}
            </div>
            <div className={`font-display text-2xl font-bold mt-1 ${k.color}`}>{k.value}</div>
          </Card>
        ))}
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Card className="p-4 border-l-4 border-l-amber-400 mb-6" data-testid="rank-alerts">
          <h3 className="font-display font-bold text-sm text-[color:var(--brand-navy)] mb-2 flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-amber-500"/> Alertes ({alerts.length})
          </h3>
          <ul className="space-y-1.5 text-xs">
            {alerts.slice(0, 5).map((a, i) => {
              const cls = a.severity === "red" ? "bg-red-50 text-red-700 border-red-200"
                : a.severity === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-amber-50 text-amber-700 border-amber-200";
              return (
                <li key={i} className={`p-2.5 rounded-lg border ${cls} flex items-center justify-between gap-3`}>
                  <span className="font-semibold truncate">{a.kw}</span>
                  <span className="text-[11px]">{a.msg}</span>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {/* Add form */}
      <Card className="p-5 border mb-6">
        <div className="flex flex-col md:flex-row gap-2 md:items-end">
          <div className="flex-1"><Label>Mot-clé</Label><Input value={keyword} onChange={e=>setKeyword(e.target.value)} placeholder="ex: installation climatisation [Ville]" data-testid="keyword-input"/></div>
          <div className="flex-1"><Label>Localisation</Label><Input value={location} onChange={e=>setLocation(e.target.value)}/></div>
          <Button onClick={add} className="bg-[color:var(--brand-red)] font-bold" data-testid="add-keyword"><Plus className="w-4 h-4 mr-1"/>Ajouter</Button>
        </div>
      </Card>

      {/* Keywords table */}
      <Card className="border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--muted))]/50 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-3">Mot-clé</th>
              <th className="text-left p-3">Localisation</th>
              <th className="text-left p-3">Position</th>
              <th className="text-left p-3">Évolution</th>
              <th className="text-left p-3">Historique</th>
              <th className="text-left p-3">Dernier check</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map(k => {
              const prev = k.positions?.[k.positions.length - 2]?.position;
              const curr = k.current_position;
              const diff = prev && curr ? prev - curr : 0;
              const Trend = diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
              const trendColor = diff > 0 ? "text-emerald-500" : diff < 0 ? "text-red-500" : "text-gray-400";
              const posBadge = !curr ? "bg-gray-100 text-gray-600"
                : curr <= 3 ? "bg-emerald-100 text-emerald-700"
                : curr <= 10 ? "bg-blue-100 text-blue-700"
                : curr <= 30 ? "bg-amber-100 text-amber-700"
                : "bg-red-100 text-red-700";
              // Invert Y axis for mini-chart so "up" = better rank
              const chartData = (k.positions || []).slice(-14).map((p, i) => ({
                i, position: p.position, invPos: p.position ? 51 - p.position : null,
              }));
              return (
                <tr key={k.id} className="border-t hover:bg-[hsl(var(--muted))]/20 cursor-pointer" onClick={() => setDetail(k)}>
                  <td className="p-3 font-semibold max-w-xs truncate">{k.keyword}</td>
                  <td className="p-3 text-xs">{k.location}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-mono font-bold text-xs ${posBadge}`}>
                      #{curr || "?"}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className={`flex items-center gap-1 text-xs font-semibold ${trendColor}`}>
                      <Trend className="w-3 h-3"/>{diff !== 0 ? Math.abs(diff) : "—"}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="w-28 h-8">
                      {chartData.length > 1 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={chartData}>
                            <Line type="monotone" dataKey="invPos" stroke="#3DB9E8" strokeWidth={1.8} dot={false}/>
                          </LineChart>
                        </ResponsiveContainer>
                      ) : <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Pas d'historique</span>}
                    </div>
                  </td>
                  <td className="p-3 text-xs font-mono whitespace-nowrap">{k.last_check ? new Date(k.last_check).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => check(k.id)} disabled={loading === k.id} title="Re-vérifier" data-testid={`check-${k.id}`}>
                        {loading === k.id ? <Loader2 className="w-3 h-3 animate-spin"/> : <RefreshCw className="w-3 h-3"/>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => del(k.id)} title="Supprimer"><Trash2 className="w-3 h-3"/></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!items.length && (
              <tr><td colSpan={7} className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">Aucun mot-clé. Ajoutez-en ci-dessus pour commencer le suivi.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* Detail modal */}
      {detail && <KeywordDetailModal k={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function KeywordDetailModal({ k, onClose }) {
  const data = (k.positions || []).map((p) => ({
    date: new Date(p.ts).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }),
    position: p.position,
  }));
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-up" onClick={onClose} data-testid="keyword-detail-modal">
      <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 bg-[color:var(--brand-navy)] text-white rounded-t-3xl">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">Mot-clé suivi</div>
              <h2 className="font-display text-2xl font-bold mt-1">{k.keyword}</h2>
              <div className="text-sm text-white/80 mt-1 flex items-center gap-2"><MapPin className="w-3.5 h-3.5"/>{k.location}</div>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">✕</button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="px-3 py-1.5 rounded-full bg-white/10 text-xs font-bold">Position actuelle : #{k.current_position || "?"}</span>
            <span className="px-3 py-1.5 rounded-full bg-white/10 text-xs">{k.positions?.length || 0} relevés</span>
          </div>
        </div>
        <div className="p-6">
          <h3 className="font-display font-bold text-[color:var(--brand-navy)] mb-3">Historique de position (Google)</h3>
          {data.length > 1 ? (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={data} margin={{ top: 5, right: 10, left: -5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb"/>
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8"/>
                  <YAxis reversed domain={[1, 50]} tick={{ fontSize: 11 }} stroke="#94a3b8" label={{ value: "Position", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#94a3b8" } }}/>
                  <RTooltip contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12 }} formatter={(v) => [`#${v}`, "Position"]}/>
                  <Line type="monotone" dataKey="position" stroke="#3DB9E8" strokeWidth={2.5} dot={{ r: 3, fill: "#3DB9E8" }}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-[hsl(var(--muted-foreground))]">Pas encore d'historique — lancez un premier check pour voir la progression.</div>
          )}
          {k.top3?.length > 0 && (
            <div className="mt-6">
              <h3 className="font-display font-bold text-[color:var(--brand-navy)] mb-3">Top 3 concurrents actuels</h3>
              <ul className="space-y-2">
                {k.top3.map((t, i) => (
                  <li key={i} className="p-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-6 h-6 rounded-full bg-[color:var(--brand-navy)] text-white text-xs font-bold flex items-center justify-center">{i+1}</span>
                      <a href={t.link} target="_blank" rel="noopener noreferrer" className="font-semibold text-[color:var(--brand-navy)] hover:text-[color:var(--brand-sky)] text-sm truncate flex-1">{t.title}</a>
                      <ExternalLink className="w-3 h-3 text-[hsl(var(--muted-foreground))]"/>
                    </div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] font-mono truncate">{t.link}</div>
                    {t.snippet && <div className="text-xs text-[hsl(var(--foreground))]/70 mt-1 line-clamp-2">{t.snippet}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function SeoAIAdmin() {
  const { settings: site } = useSettings();
  const [form, setForm] = useState({ url: "", prompt: "", target_type: "blog", keyword: "", city: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Seed city from settings once
  useEffect(() => {
    if (site && !form.city) {
      const city = site.main_city && site.main_city !== "[Votre ville]" ? site.main_city : "";
      if (city) setForm(f => ({ ...f, city }));
    }
  }, [site]); // eslint-disable-line
  const run = async () => {
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/ai/seo-generate", form);
      setResult(data);
      toast.success("Contenu généré");
    } catch (e) { toast.error("Erreur IA"); }
    finally { setLoading(false); }
  };
  return (
    <div className="p-6 md:p-10" data-testid="admin-seo-ai-page">
      <header className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">Assistant IA intégré</div>
        <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Génération SEO par IA</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Analyse un ancien site ou génère un contenu optimisé depuis un mot-clé.</p>
      </header>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5 border space-y-3">
          <div><Label>Type</Label>
            <select value={form.target_type} onChange={e=>setForm({...form, target_type: e.target.value})} className="w-full h-10 px-3 rounded-md border">
              <option value="blog">Article de blog</option>
              <option value="service">Page service</option>
              <option value="zone">Page zone locale</option>
              <option value="faq">FAQ</option>
            </select>
          </div>
          <div><Label>Mot-clé cible</Label><Input value={form.keyword} onChange={e=>setForm({...form, keyword: e.target.value})} placeholder="ex: installation climatisation [Ville]"/></div>
          <div><Label>Ville</Label><Input value={form.city} onChange={e=>setForm({...form, city: e.target.value})}/></div>
          <div><Label>URL à analyser (optionnel)</Label><Input value={form.url} onChange={e=>setForm({...form, url: e.target.value})} placeholder="https://votre-ancien-site.fr"/></div>
          <div><Label>Prompt / contexte</Label><textarea rows={4} value={form.prompt} onChange={e=>setForm({...form, prompt: e.target.value})} className="w-full p-2 border rounded-md text-sm"/></div>
          <Button onClick={run} disabled={loading} className="w-full bg-gradient-to-r from-[color:var(--brand-navy)] to-[color:var(--brand-sky)]" data-testid="run-ai-seo">{loading ? <Loader2 className="w-4 h-4 animate-spin"/> : "Générer avec IA"}</Button>
        </Card>
        <Card className="p-5 border">
          <h3 className="font-display font-bold mb-3">Résultat</h3>
          {!result && <div className="text-sm text-[hsl(var(--muted-foreground))]">Le contenu généré apparaîtra ici.</div>}
          {result && (
            <div className="space-y-3 text-sm">
              <div><strong>Titre :</strong> {result.title}</div>
              <div><strong>Meta title :</strong> {result.meta_title}</div>
              <div><strong>Meta desc :</strong> {result.meta_description}</div>
              <div><strong>Slug :</strong> <code className="bg-[hsl(var(--muted))] px-1">{result.slug}</code></div>
              <div><strong>Résumé :</strong> {result.short_description}</div>
              <div className="pt-2 border-t">
                <strong>Contenu :</strong>
                <pre className="whitespace-pre-wrap text-xs bg-[hsl(var(--muted))] p-3 rounded mt-2 max-h-80 overflow-y-auto">{result.content}</pre>
              </div>
              {result.seo && <div className="pt-2 border-t"><strong>Score SEO :</strong> {result.seo.score}/100</div>}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export function AnalyticsAdmin() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (d) => {
    setLoading(true);
    try {
      const { data } = await api.get("/analytics/summary", { params: { days: d } });
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(days); }, [days]);

  if (!data) return <div className="p-10 flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin"/> Chargement...</div>;

  const totals = data.totals || {};
  const convRate = totals.pageviews > 0 ? ((totals.devis + totals.tel_clicks) / totals.pageviews * 100) : 0;
  const telRate = totals.pageviews > 0 ? (totals.tel_clicks / totals.pageviews * 100) : 0;
  const avgPerDay = Math.round(totals.pageviews / days);

  // Merge daily_pageviews + daily_devis into unified series
  const dateSet = new Set([...(data.daily_pageviews||[]).map(d=>d.date), ...(data.daily_devis||[]).map(d=>d.date)]);
  const pvMap = Object.fromEntries((data.daily_pageviews||[]).map(d=>[d.date, d.count]));
  const dvMap = Object.fromEntries((data.daily_devis||[]).map(d=>[d.date, d.count]));
  const series = [...dateSet].sort().map(date => ({
    date,
    label: new Date(date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }),
    pageviews: pvMap[date] || 0,
    devis: dvMap[date] || 0,
  }));

  const COLORS = ["#3DB9E8", "#1E3A5F", "#E30613", "#10b981", "#f59e0b", "#8b5cf6"];

  const kpis = [
    { label: "Pages vues", value: totals.pageviews || 0, sub: `≈ ${avgPerDay}/jour`, Icon: Eye, color: "text-[color:var(--brand-sky)]" },
    { label: "Clics téléphone", value: totals.tel_clicks || 0, sub: `${telRate.toFixed(1)}% CTR`, Icon: PhoneIcon, color: "text-emerald-500" },
    { label: "Clics email", value: totals.email_clicks || 0, sub: "", Icon: MailIcon, color: "text-amber-500" },
    { label: "Leads devis", value: totals.leads || 0, sub: `${totals.devis || 0} soumissions`, Icon: Zap, color: "text-[color:var(--brand-red)]" },
    { label: "Taux conversion", value: `${convRate.toFixed(1)}%`, sub: "clics + devis / vues", Icon: TrendingUp, color: "text-violet-500" },
  ];

  return (
    <div className="p-6 md:p-10" data-testid="admin-analytics-page">
      <header className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">Analytics</div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Trafic & conversions</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Monitoring interne temps réel — privacy-friendly (aucun cookie tiers).</p>
        </div>
        <div className="flex items-center gap-1 bg-[hsl(var(--muted))] rounded-full p-1" data-testid="analytics-period-selector">
          {[7, 30, 90].map(d => (
            <button
              key={d} onClick={() => setDays(d)}
              className={`px-4 h-9 rounded-full text-sm font-semibold transition-all ${days === d ? "bg-[color:var(--brand-navy)] text-white shadow" : "text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)]"}`}
              data-testid={`period-${d}d`}
            >{d} jours</button>
          ))}
          <button onClick={() => load(days)} className="ml-1 w-9 h-9 rounded-full flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)]" title="Rafraîchir" data-testid="analytics-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4" />}
          </button>
        </div>
      </header>

      {/* KPI row */}
      <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {kpis.map((k, i) => (
          <Card key={i} className="p-5 border relative overflow-hidden" data-testid={`kpi-${k.label.toLowerCase().replace(/\s/g,'-')}`}>
            <k.Icon className={`w-5 h-5 ${k.color} mb-3`} />
            <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-semibold">{k.label}</div>
            <div className="font-display text-3xl font-bold text-[color:var(--brand-navy)] mt-1">{k.value}</div>
            {k.sub && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">{k.sub}</div>}
          </Card>
        ))}
      </div>

      {/* Time series chart */}
      <Card className="p-5 border mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-[color:var(--brand-navy)]">Évolution quotidienne</h3>
          <div className="flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[color:var(--brand-sky)]"/>Pages vues</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[color:var(--brand-red)]"/>Devis soumis</span>
          </div>
        </div>
        {series.length ? (
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <AreaChart data={series} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                <defs>
                  <linearGradient id="gPV" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3DB9E8" stopOpacity={0.45}/>
                    <stop offset="100%" stopColor="#3DB9E8" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="gDV" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#E30613" stopOpacity={0.35}/>
                    <stop offset="100%" stopColor="#E30613" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false}/>
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false}/>
                <RTooltip contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12 }} />
                <Area type="monotone" dataKey="pageviews" stroke="#3DB9E8" strokeWidth={2.5} fill="url(#gPV)" />
                <Area type="monotone" dataKey="devis" stroke="#E30613" strokeWidth={2.5} fill="url(#gDV)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="py-12 text-center text-sm text-[hsl(var(--muted-foreground))]">Aucune donnée sur la période.</div>
        )}
      </Card>

      {/* Breakdown grid */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        {/* Sources pie */}
        <Card className="p-5 border">
          <h3 className="font-display font-bold text-[color:var(--brand-navy)] mb-4">Sources de trafic</h3>
          {(data.sources||[]).length ? (
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={data.sources} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                    {data.sources.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <RTooltip contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="py-8 text-center text-xs text-[hsl(var(--muted-foreground))]">Pas encore de sources tracées.</div>
          )}
          <ul className="mt-2 space-y-1.5 text-xs">
            {(data.sources||[]).slice(0, 5).map((s, i) => (
              <li key={i} className="flex items-center justify-between">
                <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: COLORS[i%COLORS.length] }}/>{s.name || "direct"}</span>
                <strong className="tabular-nums">{s.value}</strong>
              </li>
            ))}
          </ul>
        </Card>

        {/* Top pages */}
        <Card className="p-5 border">
          <h3 className="font-display font-bold text-[color:var(--brand-navy)] mb-4">Top pages</h3>
          {data.top_pages?.length ? (
            <ul className="space-y-2">
              {data.top_pages.slice(0, 8).map((p, i) => {
                const max = data.top_pages[0]?.views || 1;
                return (
                  <li key={i} className="relative">
                    <div className="flex justify-between items-center text-xs mb-1">
                      <span className="font-mono truncate max-w-[75%]">{p.path}</span>
                      <strong className="tabular-nums">{p.views}</strong>
                    </div>
                    <div className="h-1.5 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[color:var(--brand-sky)] to-[color:var(--brand-navy)]" style={{ width: `${(p.views/max)*100}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : <div className="text-xs text-[hsl(var(--muted-foreground))]">Aucune vue encore.</div>}
        </Card>

        {/* Top cities */}
        <Card className="p-5 border">
          <h3 className="font-display font-bold text-[color:var(--brand-navy)] mb-4">Top villes (géoloc)</h3>
          {data.top_cities?.length ? (
            <ul className="space-y-2">
              {data.top_cities.slice(0, 8).map((c, i) => {
                const max = data.top_cities[0]?.views || 1;
                return (
                  <li key={i}>
                    <div className="flex justify-between items-center text-xs mb-1">
                      <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3 text-[color:var(--brand-sky)]" />{c.city}</span>
                      <strong className="tabular-nums">{c.views}</strong>
                    </div>
                    <div className="h-1.5 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600" style={{ width: `${(c.views/max)*100}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : <div className="text-xs text-[hsl(var(--muted-foreground))]">Les données de géoloc arriveront bientôt.</div>}
        </Card>
      </div>

      {/* Conversion funnel */}
      <Card className="p-5 border bg-gradient-to-br from-[color:var(--brand-navy)] to-[#0f223c] text-white">
        <h3 className="font-display font-bold mb-4 flex items-center gap-2"><Zap className="w-4 h-4"/> Funnel de conversion ({days} jours)</h3>
        <div className="grid md:grid-cols-4 gap-3">
          {(() => {
            const steps = [
              { label: "Pages vues", value: totals.pageviews || 0 },
              { label: "Clics tél/email", value: (totals.tel_clicks||0) + (totals.email_clicks||0) },
              { label: "Devis soumis", value: totals.devis || 0 },
              { label: "Leads enregistrés", value: totals.leads || 0 },
            ];
            const top = steps[0].value || 1;
            return steps.map((step, i) => {
              const pct = step.value / top * 100;
              return (
                <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">{i+1}. {step.label}</div>
                  <div className="font-display text-3xl font-bold mt-1 text-white">{step.value}</div>
                  <div className="text-xs text-white/70 mt-1">{pct.toFixed(1)}% du haut</div>
                  <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-[color:var(--brand-sky)]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </Card>
    </div>
  );
}


export function LocalSEOAdmin() {
  const [services, setServices] = useState([]);
  const [zones, setZones] = useState([]);
  const [pages, setPages] = useState([]);
  const [stats, setStats] = useState({ pages: [], by_city: [], window_days: 90 });
  const [selSvc, setSelSvc] = useState(new Set());
  const [selZone, setSelZone] = useState(new Set());
  const [useAi, setUseAi] = useState(true);
  const [overwrite, setOverwrite] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    const [sv, zn, pg, st] = await Promise.all([
      api.get("/services"), api.get("/zones"), api.get("/local-pages"),
      api.get("/analytics/local-pages").catch(() => ({ data: { pages: [], by_city: [] } })),
    ]);
    setServices(sv.data); setZones(zn.data); setPages(pg.data); setStats(st.data);
  };
  useEffect(() => { load(); }, []);

  // Quick lookup for stats per slug
  const statsBySlug = React.useMemo(() => {
    const m = {};
    (stats.pages || []).forEach(r => { m[r.slug] = r; });
    return m;
  }, [stats]);

  const toggle = (set, setter, id) => {
    const nx = new Set(set);
    nx.has(id) ? nx.delete(id) : nx.add(id);
    setter(nx);
  };
  const selectAll = (items, setter) => setter(new Set(items.map(i => i.id)));
  const clearAll = (setter) => setter(new Set());

  const run = async () => {
    if (!selSvc.size || !selZone.size) return toast.error("Sélectionnez au moins 1 service ET 1 zone");
    setRunning(true); setResult(null);
    const combinations = selSvc.size * selZone.size;
    toast.info(`Génération de ${combinations} page(s) en cours...`);
    try {
      const { data } = await api.post("/local-pages/bulk-generate", {
        service_ids: [...selSvc], zone_ids: [...selZone], use_ai: useAi, overwrite,
      });
      setResult(data);
      toast.success(`${data.created.length} page(s) créée(s), ${data.skipped.length} ignorée(s).`);
      await load();
    } catch (e) {
      toast.error("Échec de la génération. " + (e?.response?.data?.detail || ""));
    } finally { setRunning(false); }
  };

  const del = async (pid) => {
    if (!window.confirm("Supprimer cette page locale ?")) return;
    await api.delete(`/local-pages/${pid}`);
    toast.success("Page supprimée");
    await load();
  };

  const combinations = selSvc.size * selZone.size;

  return (
    <div className="p-6 md:p-10" data-testid="admin-local-seo-page">
      <header className="mb-8">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">
          <Sparkles className="w-3.5 h-3.5" /> SEO LOCAL · Générateur IA
        </div>
        <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Génération Local SEO</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          Créez automatiquement des pages SEO optimisées en croisant vos services avec vos zones d'intervention (villes × services).
        </p>
      </header>

      {/* Selection grids */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <Card className="p-5 border">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-display font-bold text-[color:var(--brand-navy)]">Services</h3>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">{selSvc.size} sélectionné(s) / {services.length}</p>
            </div>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" onClick={() => selectAll(services, setSelSvc)} data-testid="select-all-services">Tout</Button>
              <Button size="sm" variant="outline" onClick={() => clearAll(setSelSvc)}>Aucun</Button>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto space-y-1.5 pr-1">
            {services.map(s => (
              <label key={s.id} className={`flex items-center gap-2.5 p-2.5 rounded-lg cursor-pointer border text-sm transition-colors ${selSvc.has(s.id) ? "bg-[color:var(--brand-sky)]/10 border-[color:var(--brand-sky)]/50" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/50"}`}>
                <input type="checkbox" checked={selSvc.has(s.id)} onChange={() => toggle(selSvc, setSelSvc, s.id)} data-testid={`svc-cb-${s.slug}`} />
                <span className="font-semibold text-[color:var(--brand-navy)] flex-1">{s.title}</span>
                {s.keyword && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] font-mono">{s.keyword}</span>}
              </label>
            ))}
            {!services.length && <div className="text-sm text-[hsl(var(--muted-foreground))] p-2">Aucun service — créez-en dans /admin/services</div>}
          </div>
        </Card>

        <Card className="p-5 border">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-display font-bold text-[color:var(--brand-navy)]">Zones / Villes</h3>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">{selZone.size} sélectionnée(s) / {zones.length}</p>
            </div>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" onClick={() => selectAll(zones, setSelZone)} data-testid="select-all-zones">Tout</Button>
              <Button size="sm" variant="outline" onClick={() => clearAll(setSelZone)}>Aucun</Button>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto space-y-1.5 pr-1">
            {zones.map(z => (
              <label key={z.id} className={`flex items-center gap-2.5 p-2.5 rounded-lg cursor-pointer border text-sm transition-colors ${selZone.has(z.id) ? "bg-[color:var(--brand-sky)]/10 border-[color:var(--brand-sky)]/50" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/50"}`}>
                <input type="checkbox" checked={selZone.has(z.id)} onChange={() => toggle(selZone, setSelZone, z.id)} data-testid={`zone-cb-${z.slug}`} />
                <MapPin className="w-3.5 h-3.5 text-[color:var(--brand-sky)]" />
                <span className="font-semibold text-[color:var(--brand-navy)] flex-1">{z.city}</span>
                <span className="text-[10px] font-mono text-[hsl(var(--muted-foreground))]">{z.postal_code}</span>
              </label>
            ))}
            {!zones.length && <div className="text-sm text-[hsl(var(--muted-foreground))] p-2">Aucune zone — créez-en dans /admin/zones</div>}
          </div>
        </Card>
      </div>

      {/* Options + Generate button */}
      <Card className="p-5 border mb-6 bg-gradient-to-br from-[color:var(--brand-navy)] to-[#0f223c] text-white">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><Wand2 className="w-4 h-4" /> Lancer la génération</h3>
            <p className="text-white/70 text-xs mt-1">
              <strong className="text-white">{combinations}</strong> combinaison(s) à générer
              {useAi ? " via l'assistant IA" : " via template local"}.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={useAi} onChange={e => setUseAi(e.target.checked)} data-testid="use-ai-toggle" />
            <span>Générer avec IA</span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={overwrite} onChange={e => setOverwrite(e.target.checked)} data-testid="overwrite-toggle" />
            <span>Écraser l'existant</span>
          </label>
          <Button onClick={run} disabled={running || !combinations} className="bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/90 h-11 px-6 font-bold" data-testid="run-bulk-gen">
            {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération...</> : <><Sparkles className="w-4 h-4 mr-2" /> Générer ({combinations})</>}
          </Button>
        </div>
        {result && (
          <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-3 gap-3 text-sm">
            <div><span className="text-white/60 text-xs uppercase tracking-wider">Créées</span><div className="font-display text-2xl font-bold text-emerald-300">{result.created.length}</div></div>
            <div><span className="text-white/60 text-xs uppercase tracking-wider">Ignorées</span><div className="font-display text-2xl font-bold text-amber-300">{result.skipped.length}</div></div>
            <div><span className="text-white/60 text-xs uppercase tracking-wider">Erreurs</span><div className="font-display text-2xl font-bold text-red-300">{result.errors.length}</div></div>
          </div>
        )}
      </Card>

      {/* Existing pages */}
      <Card className="border overflow-x-auto">
        <div className="p-5 border-b flex items-center justify-between">
          <h3 className="font-display font-bold text-[color:var(--brand-navy)]">Pages générées <span className="text-sm font-normal text-[hsl(var(--muted-foreground))]">({pages.length})</span></h3>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Stats sur {stats.window_days || 90} j</span>
            <Button size="sm" variant="outline" onClick={load}><RefreshCw className="w-3 h-3 mr-1" /> Rafraîchir</Button>
          </div>
        </div>

        {/* Audience par ville (SEO local) */}
        {(stats.by_city || []).length > 0 && (
          <div className="p-5 border-b bg-[hsl(var(--muted))]/20">
            <div className="text-xs uppercase tracking-wider text-[color:var(--brand-navy)] font-bold mb-3 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-[color:var(--brand-sky)]" /> Audience par ville / code postal
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="local-seo-city-stats">
              {stats.by_city.slice(0, 8).map((c, i) => (
                <div key={i} className="p-3 rounded-lg bg-white border border-[hsl(var(--border))] hover:border-[color:var(--brand-sky)] transition">
                  <div className="flex items-baseline justify-between">
                    <div className="font-display font-bold text-[color:var(--brand-navy)] text-sm truncate">{c.city || "—"}</div>
                    <div className="text-[10px] font-mono text-[hsl(var(--muted-foreground))]">{c.postal_code || ""}</div>
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{c.pages_count} page{c.pages_count > 1 ? "s" : ""}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-[color:var(--brand-sky)]" title="Vues de page">{c.views} vues</span>
                      <span className="text-xs font-bold text-[color:var(--brand-red)]" title="Clics depuis lien ville">{c.clicks} clics</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--muted))]/50 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-3">Titre</th>
              <th className="text-left p-3">Ville · CP</th>
              <th className="text-left p-3">Service</th>
              <th className="text-left p-3">SEO</th>
              <th className="text-left p-3">Vues</th>
              <th className="text-left p-3">Clics</th>
              <th className="text-left p-3">MAJ</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {pages.map(p => {
              const st = statsBySlug[p.slug] || { views: 0, city_clicks: 0, page_clicks: 0 };
              const clicks = (st.city_clicks || 0) + (st.page_clicks || 0);
              return (
                <tr key={p.id} className="border-t hover:bg-[hsl(var(--muted))]/20">
                  <td className="p-3 font-semibold max-w-md truncate">{p.title || p.slug}</td>
                  <td className="p-3 text-xs"><MapPin className="w-3 h-3 inline mr-1 text-[color:var(--brand-sky)]" />{p.city} · <span className="font-mono">{p.postal_code}</span></td>
                  <td className="p-3 text-xs">{p.service_title}</td>
                  <td className="p-3"><span className={`inline-block px-2 py-0.5 rounded-full font-mono font-bold text-xs ${p.seo?.score >= 80 ? "bg-emerald-100 text-emerald-700" : p.seo?.score >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{p.seo?.score ?? "?"}</span></td>
                  <td className="p-3 text-xs font-bold text-[color:var(--brand-sky)] tabular-nums" data-testid={`local-page-views-${p.slug}`}>{st.views}</td>
                  <td className="p-3 text-xs font-bold text-[color:var(--brand-red)] tabular-nums" data-testid={`local-page-clicks-${p.slug}`}>{clicks}</td>
                  <td className="p-3 text-xs font-mono">{p.updated_at ? new Date(p.updated_at).toLocaleDateString("fr-FR") : "—"}</td>
                  <td className="p-3 flex gap-1">
                    <Link to={`/intervention/${p.slug}`} target="_blank">
                      <Button size="sm" variant="outline" title="Voir la page"><ExternalLink className="w-3 h-3" /></Button>
                    </Link>
                    <Button size="sm" variant="outline" onClick={() => del(p.id)} title="Supprimer"><Trash2 className="w-3 h-3" /></Button>
                  </td>
                </tr>
              );
            })}
            {!pages.length && (
              <tr><td colSpan={8} className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">Aucune page locale générée. Sélectionnez des services et zones ci-dessus, puis lancez la génération.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
